import { Field, Formik } from 'formik';
import {
	CerebroDrawer,
	CerebroHeading,
	CerebroForm,
	CerebroLabel,
	CerebroTextarea,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import { Grid, Link } from '@mui/material';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';
import { DealStatusView } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

interface DealSuccessStoryDrawerProps {
	show: boolean;
	data: DealStatusView;
	onClose: () => void;
}

export const DealSuccessStoryDrawer: React.FC<DealSuccessStoryDrawerProps> = ({ show, data, onClose }) => {
	const api = useApi();
	const snackbar = useSnackbar();

	const saveStoryMutation = useMutation(
		async ({ values }: FormikUtils.MutateOnSubmitVariables<DealSuccessStoryFormValues>) => {
			const payload = {
				reasonForUsingCerebro: values.reasonForUsingCerebro,
				whySelectedTermSheet: values.whySelectedTermSheet,
				topDealChallenge1: values.topDealChallenge1,
				topDealChallenge2: values.topDealChallenge2,
				topDealChallenge3: values.topDealChallenge3,
				dealSummary: values.dealSummary,
			};

			const { data: response, error } = await getApiResponseData<void>(
				api.patch(CerebroApiEndpoints.Deals.SuccessStory(data.deal.guid), payload)
			);

			if (error) throw new Error(error.response?.data ?? 'Error saving success story');
			return response;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	const regenerateSummaryMutation = useMutation(async (values: DealSuccessStoryFormValues) => {
		const { data, error } = await getApiResponseData<{ summary: string }>(
			api.post(CerebroApiEndpoints.Deals.GenerateDealSummary(values.dealGuid))
		);
		if (error) throw new Error(error.response?.data ?? 'Error regenerating summary');
		return data;
	});

	const handleRegenerateSummary = async (
		values: DealSuccessStoryFormValues,
		setFieldValue: (field: string, value: any) => void
	) => {
		if (regenerateSummaryMutation.isLoading) return;

		try {
			// show feedback & disable UI
			snackbar.enqueueSnackbar('Regenerating summary...', { variant: 'info' });
			regenerateSummaryMutation.reset();
			regenerateSummaryMutation.mutateAsync(values, {
				onSuccess: data => {
					setFieldValue('dealSummary', data?.summary);
					snackbar.enqueueSnackbar('Summary regenerated successfully.', { variant: 'success' });
				},
				onError: (error: any) => {
					snackbar.enqueueSnackbar(error.message ?? 'Error regenerating summary', { variant: 'error' });
				},
				onSettled: () => {
					// Formik manages isSubmitting automatically via loading states in components
				},
			});
		} catch (error: any) {
			snackbar.enqueueSnackbar(error.message ?? 'Error regenerating summary', { variant: 'error' });
		}
	};

	const handleDownload = () => {
		window.open(CerebroApiEndpoints.Deals.SuccessStory(data.deal.guid), '_blank');
	};

	return (
		<CerebroDrawer open={show} onClose={onClose}>
			<CerebroHeading>Deal Success Story</CerebroHeading>

			<CerebroText variant="hint" component="span">
				Use the form below to enter or update the deal success information for this deal, including the reasons
				the borrower chose Cerebro, why they picked the winning term sheet, and the top challenges for the deal.
				This information will be incorporated in the deal success story document.
			</CerebroText>

			<Formik
				enableReinitialize
				initialValues={{
					dealGuid: data.deal.guid,
					reasonForUsingCerebro: data.deal.reasonForUsingCerebro ?? '',
					whySelectedTermSheet: data.deal.whySelectedTermSheet ?? '',
					topDealChallenge1: data.deal.topDealChallenge1 ?? '',
					topDealChallenge2: data.deal.topDealChallenge2 ?? '',
					topDealChallenge3: data.deal.topDealChallenge3 ?? '',
					dealSummary: data.deal.dealSummary ?? '',
				}}
				onSubmit={FormikUtils.MutateOnSubmit(saveStoryMutation.mutate)}
			>
				{({ values, isSubmitting, handleSubmit, setFieldValue }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={12}>
								<CerebroLabel htmlFor="reasonForUsingCerebro">Reason for using Cerebro</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="reasonForUsingCerebro"
									name="reasonForUsingCerebro"
									rows={3}
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="whySelectedTermSheet">
									Why they selected the chosen term sheet
								</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="whySelectedTermSheet"
									name="whySelectedTermSheet"
									rows={3}
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="topDealChallenge1">Top 3 Deal Challenges</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="topDealChallenge1"
									name="topDealChallenge1"
									rows={2}
									placeholder="Challenge 1"
									disabled={isSubmitting}
								/>
								<Field
									as={CerebroTextarea}
									id="topDealChallenge2"
									name="topDealChallenge2"
									rows={2}
									placeholder="Challenge 2"
									disabled={isSubmitting}
								/>
								<Field
									as={CerebroTextarea}
									id="topDealChallenge3"
									name="topDealChallenge3"
									rows={2}
									placeholder="Challenge 3"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="dealSummary">Deal Summary</CerebroLabel>
								<CerebroText variant="hint">
									This summary is generated by AI. You can edit it manually or click Regenerate to
									replace it.
								</CerebroText>
								<Field
									as={CerebroTextarea}
									id="dealSummary"
									name="dealSummary"
									rows={5}
									disabled={isSubmitting || regenerateSummaryMutation.isLoading}
								/>

								<Link
									sx={{
										display: 'inline-block',
										mt: 1,
										cursor: regenerateSummaryMutation.isLoading ? 'not-allowed' : 'pointer',
										fontSize: 14,
										color: regenerateSummaryMutation.isLoading ? 'gray' : 'black',
										fontWeight: 500,
										pointerEvents: regenerateSummaryMutation.isLoading ? 'none' : 'auto',
									}}
									onClick={() => handleRegenerateSummary(values, setFieldValue)}
								>
									{regenerateSummaryMutation.isLoading ? 'Regenerating…' : 'Regenerate Summary'}
								</Link>
								<FontAwesomeIcon
									icon={faRefresh}
									size="xs"
									spin={regenerateSummaryMutation.isLoading}
								/>
							</Grid>

							<Grid item xs={12} sx={{ mt: 3 }}>
								<CerebroPrimaryButton
									type="submit"
									loading={isSubmitting || regenerateSummaryMutation.isLoading}
								>
									Save
								</CerebroPrimaryButton>
								<CerebroSecondaryButton sx={{ ml: 2 }} onClick={handleDownload} disabled={isSubmitting}>
									Download PDF
								</CerebroSecondaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface DealSuccessStoryFormValues {
	dealGuid: string;
	reasonForUsingCerebro: string;
	whySelectedTermSheet: string;
	topDealChallenge1: string;
	topDealChallenge2: string;
	topDealChallenge3: string;
	dealSummary: string;
}
